import { ImageFill, SvgGraphics } from '@tencent/alloy-renderer';
import {
    getPlaceHolderType,
    getSize,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { PlaceHolderMode } from '@src/slide/domain/ui-model/element-modeles/property/placeholder/PlaceHolderMode';
import PictureIcon from '@editor/src/img/pageIndex/ph-pic.svg';
import PictureIconHover from '@editor/src/img/pageIndex/ph-pic-hover.svg';
import TableIcon from '@editor/src/img/pageIndex/ph-tbl.svg';
import TableIconHover from '@editor/src/img/pageIndex/ph-tbl-hover.svg';
import { generatePlaceHolderModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/utils';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import imageCache from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache';
import { RenderPostionType } from '@src/js/constant';
import { CANVAS_PLACEHOLDER_IMG_ID, CANVAS_PLACEHOLDER_TABLE_ID } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import { BorderType } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { IReactionDisposer, reaction } from 'mobx';
import { ComponentTypes } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/component-types';
import { BaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component';

const ICON_WIDTH = 72;
const ICON_HEIGHT = 60;
const ICON_OFFSET = 40;

interface IPlaceholderIconEventHandlers {
    handleIconClick: (type) => void;
}

/**
 * 编辑区 - 吸附元素容器，包含透明触发区
 */
export class PlaceholderElement extends BaseComponent {
    private tableIconComponents?: SvgGraphics;
    private picIconComponents?: SvgGraphics;
    private brightPlaceHolderBorder: GraphicsRenderComponent | null;
    private darkPlaceHolderBorder: GraphicsRenderComponent | null;
    private actionDispose: IReactionDisposer;
    private eventHandlers?: IPlaceholderIconEventHandlers;

    public constructor(private factoryOptions: InterfaceFactoryOptions<IElementBaseModel>) {
        super();
        this.renderPlaceHolderBorder();
        this.renderPlaceholderIcon();
        factoryOptions.logicService.setComponentLogic(factoryOptions, this);
        if (this.hasPlaceholderIcon(factoryOptions.model)) {
            this.actionDispose = reaction(
                () => (this.factoryOptions.editAreaCanvasSizeManager?.getCanvasScale()),
                () => {
                    this.updatePlaceholder();
                },
            );
        }
    }

    public getUniqueId(): string {
        return `${this.factoryOptions.model.key}-${getPlaceHolderType(this.factoryOptions.model)}-${this.getComponentType()}`;
    }

    public getComponentType(): string {
        return ComponentTypes.PLACEHOLDERICON;
    }

    public setEventHandlers(eventHandlers: IPlaceholderIconEventHandlers) {
        this.eventHandlers = eventHandlers;
    }

    public updatePlaceholder() {
        this.renderPlaceHolderBorder();
        this.renderPlaceholderIcon();
    }

    public dispose = () => {
        super.dispose();
        this.clear();
        this.actionDispose?.();
        this.factoryOptions.logicService.removeComponentLogic(this);
    };

    /**
     * 渲染占位符图标
     * @private
     */
    private renderPlaceholderIcon() {
        const type = getPlaceHolderType(this.factoryOptions.model);
        // 母版、版式中不允许点击icon添加xx占位符
        const interactive = !!this.factoryOptions.model.isFromSlide;
        let position;
        switch (type) {
            case 'tbl':
                position = this.getIconPosition(1, 0);
                this.renderTableIcon(position.left, position.top, interactive);
                return;
            case 'pic':
                position = this.getIconPosition(1, 0);
                this.renderImgIcon(position.left, position.top, interactive);
                return;
            case 'txt':
                position = this.getIconPosition(2, 0);
                this.renderTableIcon(position.left, position.top, interactive);
                position = this.getIconPosition(2, 1);
                this.renderImgIcon(position.left, position.top, interactive);
                return;
            default:
                return;
        }
    }

    private hasPlaceholderIcon(model: IElementBaseModel): boolean {
        const type = getPlaceHolderType(model);
        return type === PlaceHolderMode.PIC || type === PlaceHolderMode.TBL || type === PlaceHolderMode.TXT;
    }

    /**
     * 计算每个icon占位符的位置
     * @param iconNumber icon的数量
     * @param iconIndex 第几个icon
     * @private
     */
    private getIconPosition(iconNumber, iconIndex) {
        const { width, height } = this.getElementSize();
        const top = (height - this.iconHeight) / 2 ;
        let left = 0;

        // 一个icon
        if (iconNumber === 1) {
            left = (width - this.iconWidth) / 2;
        } else {
            // 两个icon
            if (iconIndex === 0) {
                left = (width - ((this.iconWidth * 2) + this.iconOffset)) / 2;
            } else {
                left = ((width - ((this.iconWidth * 2) + this.iconOffset)) / 2) + this.iconWidth + this.iconOffset;
            }
        }

        return {
            top,
            left,
        };
    }

    private get iconWidth() {
        const { width } = this.getElementSize();
        const scale = ptToCanvas(this.factoryOptions.editAreaCanvasSizeManager?.getCanvasScale() ?? 1);
        const iconWidth = ICON_WIDTH / scale;
        const maxWidth = width / 4;
        return iconWidth > maxWidth ? maxWidth : iconWidth;
    }

    private get iconHeight() {
        return this.iconWidth / (ICON_WIDTH / ICON_HEIGHT);
    }

    private get iconOffset() {
        const { width } = this.getElementSize();
        const scale = ptToCanvas(this.factoryOptions.editAreaCanvasSizeManager?.getCanvasScale() ?? 1);
        const iconOffset = ICON_OFFSET / scale;
        const maxOffset = width / 10;
        return iconOffset > maxOffset ? maxOffset : iconOffset;
    }

    private clear() {
        if (this.tableIconComponents) {
            this.removeChild(this.tableIconComponents);
            this.tableIconComponents.dispose?.();
            this.tableIconComponents = undefined;
        }
        if (this.picIconComponents) {
            this.removeChild(this.picIconComponents);
            this.picIconComponents.dispose?.();
            this.picIconComponents = undefined;
        }
    }

    private renderPlaceHolderBorder() {
        // ssr的情况下不渲染占位符border
        if (this.hasBorder() || __IS_SSR__) {
            this.removePlaceHolderBorder();
            return;
        }
        const size = this.getElementSize();
        const graphicsModels = generatePlaceHolderModel(
            size,
            this.factoryOptions.region === RenderPostionType.TEMPLATE,
            this.factoryOptions.getPageScale?.(),
        );
        if (!this.brightPlaceHolderBorder) {
            this.brightPlaceHolderBorder = new GraphicsRenderComponent(graphicsModels[0]);
            this.addChild(this.brightPlaceHolderBorder);
        } else {
            this.brightPlaceHolderBorder.updateModel(graphicsModels[0]);
        }
        if (!this.darkPlaceHolderBorder) {
            this.darkPlaceHolderBorder = new GraphicsRenderComponent(graphicsModels[1]);
            this.addChild(this.darkPlaceHolderBorder);
        } else {
            this.darkPlaceHolderBorder.updateModel(graphicsModels[1]);
        }
    }

    private removePlaceHolderBorder() {
        if (this.brightPlaceHolderBorder) {
            this.removeChild(this.brightPlaceHolderBorder);
            this.brightPlaceHolderBorder.dispose();
            this.brightPlaceHolderBorder = null;
        }
        if (this.darkPlaceHolderBorder) {
            this.removeChild(this.darkPlaceHolderBorder);
            this.darkPlaceHolderBorder.dispose();
            this.darkPlaceHolderBorder = null;
        }
    }

    private hasBorder(): boolean {
        const { model } = this.factoryOptions;
        const borderType = model.getBorder()?.getBorderType();
        return borderType !== BorderType.NoFill;
    }

    private getElementSize() {
        const { model, frame } = this.factoryOptions;
        return getSize(frame, model.props);
    }

    /**
     * 渲染图片占位符
     * @param x
     * @param y
     * @interactive 可交互的
     * @private
     */
    private renderImgIcon(x, y, interactive = true) {
        const width = this.iconWidth;
        const height = this.iconHeight;
        const path = `M ${x} ${y} L ${x + width} ${y} L ${x + width} ${y + height} L ${x} ${y + height} z`;
        if (!this.picIconComponents) {
            const iconGraphics = new SvgGraphics(path);
            iconGraphics.id = CANVAS_PLACEHOLDER_IMG_ID;
            const image = imageCache.get({
                alloyParams: { urlOrImage: PictureIcon },
            });
            iconGraphics.fill = new ImageFill(image);
            this.picIconComponents = iconGraphics;
            if (interactive) {
                this.picIconComponents.onMouseEnter(() => {
                    const image = imageCache.get({
                        alloyParams: { urlOrImage: PictureIconHover },
                    });
                    iconGraphics.fill = new ImageFill(image);
                });
                this.picIconComponents.onMouseLeave(() => {
                    const image = imageCache.get({
                        alloyParams: { urlOrImage: PictureIcon },
                    });
                    iconGraphics.fill = new ImageFill(image);
                });
                this.picIconComponents.onClick(() => {
                    this.eventHandlers?.handleIconClick(PlaceHolderMode.PIC);
                });
            }
            this.addChild(iconGraphics);
        } else {
            this.picIconComponents.path = path;
        }
        this.setPointer(this.picIconComponents, interactive);
    }

    /**
     * 渲染表格占位符
     * @param x
     * @param y
     * @interactive 可交互的
     * @private
     */
    private renderTableIcon(x, y, interactive = true) {
        const width = this.iconWidth;
        const height = this.iconHeight;
        const path = `M ${x} ${y} L ${x + width} ${y} L ${x + width} ${y + height} L ${x} ${y + height} z`;
        if (!this.tableIconComponents) {
            const iconGraphics = new SvgGraphics(path);
            iconGraphics.id = CANVAS_PLACEHOLDER_TABLE_ID;
            const image = imageCache.get({
                alloyParams: { urlOrImage: TableIcon },
            });
            iconGraphics.fill = new ImageFill(image);
            this.tableIconComponents = iconGraphics;
            if (interactive) {
                this.tableIconComponents.onMouseEnter(() => {
                    const image = imageCache.get({
                        alloyParams: { urlOrImage: TableIconHover },
                    });
                    iconGraphics.fill = new ImageFill(image);
                });
                this.tableIconComponents.onMouseLeave(() => {
                    const image = imageCache.get({
                        alloyParams: { urlOrImage: TableIcon },
                    });
                    iconGraphics.fill = new ImageFill(image);
                });
                this.tableIconComponents.onClick(() => {
                    this.eventHandlers?.handleIconClick(PlaceHolderMode.TBL);
                });
            }
            this.addChild(iconGraphics);
        } else {
            this.tableIconComponents.path = path;
        }
        this.setPointer(this.tableIconComponents, interactive);
    }

    private setPointer(components: SvgGraphics, interactive: boolean) {
        if (!components) return;
        if (interactive) {
            components.cursor = 'pointer';
        } else {
            components.cursor = 'default';
        }
    }
}
